if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.section = this.closest('.js-product');
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.stockBadge = this.querySelector('.js-stock-badge');

      // Bind to on:variant:change event for this product
      this.section.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.getVariantInventory();
        const variantInventory = inventory.find((variant) => variant.id === evt.detail.variant.id);
        if (variantInventory) {
          this.updateInventory(
            variantInventory.inventory_quantity,
            variantInventory.available,
            variantInventory.inventory_policy
          );
          this.updateStockBadge(variantInventory);
        }
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    /**
     * Updates the stock badge to match the selected variant (mirrors the Liquid badge logic)
     * @param {object} variantInventory - inventory data for the selected variant
     */
    updateStockBadge(variantInventory) {
      if (!this.stockBadge) return;

      // Same rules, same order, as the Liquid badge in snippets/main-product-inventory.liquid.
      // The brand message comes from the admin (Brand stock message metaobject) via data attributes.
      const isLava = this.stockBadge.dataset.isLava === 'true';
      const vendor = this.stockBadge.dataset.vendor || '';
      const brandMessage = this.stockBadge.dataset.brandMessage;
      const brandOverride = this.stockBadge.dataset.brandOverride === 'true';
      const escapeHtml = (text) => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
      const green = (text) => `<i class="fa fa-check-circle" aria-hidden="true"></i> <span class="product-inventory__urgency green">${text}</span>`;

      // Incoming stock: same reading of custom.incoming_stock as the Liquid (see the comment there)
      const stockQty = variantInventory.inventory_management ? (variantInventory.raw_quantity || 0) : 0;
      let oversold = stockQty < 0 ? -stockQty : 0;
      let incoming = null;
      for (const batch of Array.isArray(variantInventory.incoming) ? variantInventory.incoming : []) {
        const left = (batch.qty || 0) - oversold;
        if (left > 0) {
          if (!incoming) incoming = { ...batch, qty: left };
          oversold = 0;
        } else {
          oversold = -left;
        }
      }
      let incomingLine = '';
      if (incoming) {
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const [y, m, d] = String(incoming.date || '').split('-').map(Number);
        const when = !incoming.date || incoming.date < today ? 'Arriving soon' : `Arriving by ${d} ${months[m - 1]} ${y}`;
        incomingLine = `<i class="fa fa-clock-o" aria-hidden="true"></i> <span class="product-inventory__urgency incoming">${incoming.qty} In Production - ${when}</span>`;
      }
      // Branch line, only with incoming stock: Niori's own lights by rule (alabaster HK, the rest
      // UK, set in the Liquid as data-niori-branch); other brands from the transfer's country.
      const nioriBranch = this.stockBadge.dataset.nioriBranch || '';
      const country = (nioriBranch || String((incoming && incoming.country) || '')).toLowerCase();
      const showBranch = variantInventory.available && !!incoming && /^[a-z]{2}$/.test(country);
      const branchHtml = showBranch
        ? `<img class="incoming-flag" src="https://cdn.shopify.com/static/images/flags/${country}.svg" width="18" height="12" alt=""> ${country === 'gb' ? 'UK' : country.toUpperCase()} Branch`
        : '';
      const incomingNote = this.querySelector('.js-incoming-note');
      if (incomingNote) incomingNote.innerHTML = variantInventory.available && incoming && stockQty > 0 ? incomingLine : '';
      const incomingBranch = this.querySelector('.js-incoming-branch');
      if (incomingBranch) incomingBranch.innerHTML = branchHtml;

      let badgeHtml;
      if (!variantInventory.available) {
        badgeHtml = '<i class="fa fa-exclamation-circle red" aria-hidden="true"></i> <span class="product-inventory__urgency red">Out of Stock</span>';
      } else if (incoming && stockQty <= 0) {
        badgeHtml = incomingLine;
      } else if (brandMessage && brandOverride) {
        badgeHtml = green(escapeHtml(brandMessage));
      } else if (variantInventory.inventory_management && variantInventory.inventory_quantity > 0 && !isLava) {
        // "Ready to Ship" unless the brand sets an In stock note (e.g. Trio: "Ships in approx. 4 weeks")
        badgeHtml = green(`${variantInventory.inventory_quantity} In Stock - ${escapeHtml(this.stockBadge.dataset.inStockNote || 'Ready to Ship')}`);
      } else if (brandMessage) {
        badgeHtml = green(escapeHtml(brandMessage));
      } else if (vendor === 'Niori') {
        badgeHtml = green('Made to Order - Ships in 3-5 weeks');
      } else {
        badgeHtml = '<i class="fa fa-check-circle" aria-hidden="true"></i> <span class="product-inventory__urgency green flow">Available to Order - <a href="https://api.whatsapp.com/send?phone=447541074610" target="_blank" rel="noopener">Contact us</a> for current stock and delivery times</span>';
      }
      this.stockBadge.innerHTML = badgeHtml;
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    updateInventory(count, available, inventoryPolicy) {
      if (count === 0 && !available && this.hidden) {
        return;
      }

      let inventoryLevel;

      if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Determine whether to show the count or not
        if (inventoryLevel === 'backordered') {
          this.inventoryNotice.innerText = '';
        } else if (inventoryLevel !== 'in_stock' && (this.dataset.showCount === 'always' || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')))) {
          this.inventoryNotice.innerText = theme.strings.onlyXLeft.replace('[quantity]', count);
        } else if (inventoryLevel === 'very_low') {
          this.inventoryNotice.innerText = theme.strings.veryLowStock;
        } else if (inventoryLevel === 'low') {
          this.inventoryNotice.innerText = theme.strings.lowStock;
        } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
          this.inventoryNotice.innerText = theme.strings.inStock;
        } else if (inventoryLevel === 'none') {
          this.inventoryNotice.innerText = theme.strings.noStock;
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
